import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useLoaderData } from '@remix-run/react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { twMerge } from 'tailwind-merge';
import type { loader as rootLoader } from '~/routes/_index.tsx';
import type { FeedEntry } from '~/services/layout';
import UiComponent from '~/components/ui-component.tsx';
import { ButtonCell } from '../../button_cell/components';
import { ArrowButton } from '~/services/plugins/quick-brick-tabs/components/arrow-button';
import { useTabsResponsiveValues } from './use-tabs-responsive-values';
import {
  getTabItemSizingClassName,
  getTabItemsContainerClassName,
  getTabItemsAlignmentClassName,
  getBorderStyleClassName,
  getTabItemInnerAlignmentClassName,
} from '../utils/get-tab-item-sizing';
import { triggerTabScrollAction } from '../utils/trigger-tab-scroll-action';
import { useTabScrollObserver, useTabCentering } from '../hooks';
import { isEmptyArray } from '~/services/layout/layout';

/**
 * Tabs Screen.
 * Plugin identifier: quick-brick-tabs
 */
export function TabsScreen(props: any): JSX.Element {
  const { screen, screens, tabs, contentTypes, tabsContentFeeds } =
    useLoaderData<typeof rootLoader>();
  const [tabIndex, setTabIndex] = useState(0);

  const styles = screen.styles;

  const {
    stackingSwitch,
    stickySwitch,
    arrowsSwitch,
    blendingSwitch,
    shadowSwitch,
    parentShadowSwitch,
    gutter,
    paddingLeft,
    paddingRight,
    componentMaxWidth,
    maxWidthSwitch,
    tabsItemAlignment,
    tabItemInnerAlignment,
    tabItemSize,
    borderParentContainer,
    borderTabsContainer,
    backgroundBlurSwitch,
    parentBackgroundBlurSwitch,
  } = useTabsResponsiveValues(styles);

  const scrollable = useMemo(
    () => (!stackingSwitch ? 'truncate' : ''),
    [stackingSwitch]
  );

  const overflowClasses = useMemo(
    () =>
      stackingSwitch
        ? 'overflow-y-auto overflow-x-hidden'
        : 'overflow-y-hidden overflow-x-scroll',
    [stackingSwitch]
  );

  const stackingClasses = useMemo(
    () => (stackingSwitch ? 'flex flex-wrap' : ''),
    [stackingSwitch]
  );

  const verticalGutterClass = useMemo(
    () => (stackingSwitch ? 'tab-container-vertical-gutter' : ''),
    [stackingSwitch]
  );

  const sticky = useMemo(
    () => (stickySwitch ? 'tab-bar-sticky-classes' : ''),
    [stickySwitch]
  );

  const blending = useMemo(
    () => (blendingSwitch ? 'tab-mask-blending' : ''),
    [blendingSwitch]
  );

  const maxWidth = useMemo(
    () => (maxWidthSwitch ? 'tabs-container-max-width' : ''),
    [maxWidthSwitch]
  );

  const shadowStyles = useMemo(
    () => (shadowSwitch ? 'tabs-container-shadow' : ''),
    [shadowSwitch]
  );

  const parentShadowStyles = useMemo(
    () => (parentShadowSwitch ? 'tabs-parent-container-shadow' : ''),
    [parentShadowSwitch]
  );

  const backgroundBlurStyles = useMemo(
    () => (backgroundBlurSwitch ? 'tabs-container-backdrop-filter' : ''),
    [backgroundBlurSwitch]
  );

  const parentBackgroundBlurStyles = useMemo(
    () => (parentBackgroundBlurSwitch ? 'tabs-parent-backdrop-filter' : ''),
    [parentBackgroundBlurSwitch]
  );

  const borderParentContainerStyle = useMemo(
    () =>
      getBorderStyleClassName(
        'tabs-container-parent-border',
        borderParentContainer as string
      ),
    [borderParentContainer]
  );

  const borderTabsContainerStyle = useMemo(
    () =>
      getBorderStyleClassName(
        'tabs-container-border',
        borderTabsContainer as string
      ),
    [borderTabsContainer]
  );

  const tabListRef = useRef<HTMLDivElement>(null);

  const scrollTabs = (direction: 'left' | 'right') => {
    triggerTabScrollAction({
      ref: tabListRef,
      gutter: gutter || 0,
      paddingLeft: paddingLeft || 0,
      paddingRight: paddingRight || 0,
      componentMaxWidth,
      tabItemSize: styles.tab_item_size_select as string,
      direction,
    });
  };

  const handleTabSelect = (index: number) => {
    setTabIndex(index);
  };

  // Handle scroll state observation
  const { canScrollLeft, canScrollRight } = useTabScrollObserver(tabListRef);

  const arrows = useMemo(
    () =>
      arrowsSwitch && (canScrollLeft || canScrollRight)
        ? 'tab-arrows-enabled'
        : 'tab-arrows-disabled',
    [arrowsSwitch, canScrollLeft, canScrollRight]
  );

  // Handle tab centering after tab index changes
  useTabCentering(tabListRef, tabIndex);

  const tabCellIds = useMemo(
    () => [
      screen.styles.button_cell_plugin_configuration_id_mobile as string,
      screen.styles.button_cell_plugin_configuration_id_tablet as string,
      screen.styles.button_cell_plugin_configuration_id_desktop as string,
      screen.styles.button_cell_plugin_configuration_id_large_desktop as string,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [screen.styles]
  );

  const tabItemSizingClassName = useMemo(
    () => getTabItemSizingClassName(tabItemSize as string),
    [tabItemSize]
  );

  const tabItemsContainerClassName = useMemo(
    () => getTabItemsContainerClassName(tabItemSize as string),
    [tabItemSize]
  );

  const [shouldApplyAlignment, setShouldApplyAlignment] = useState(true);

  const tabItemsAlignmentClassName = useMemo(
    () =>
      shouldApplyAlignment
        ? getTabItemsAlignmentClassName(tabsItemAlignment as string)
        : '',
    [tabsItemAlignment, shouldApplyAlignment]
  );

  const tabItemInnerAlignmentClassName = useMemo(
    () => getTabItemInnerAlignmentClassName(tabItemInnerAlignment as string),
    [tabItemInnerAlignment]
  );

  // Check if tabs overflow the container and conditionally apply alignment
  useEffect(() => {
    if (stackingSwitch) {
      setShouldApplyAlignment(true);
      return;
    }

    const hasOverflow = canScrollLeft || canScrollRight;
    const shouldAlign = !hasOverflow;

    setShouldApplyAlignment((prev) => {
      if (prev !== shouldAlign) {
        return shouldAlign;
      }
      return prev;
    });
  }, [stackingSwitch, canScrollLeft, canScrollRight]);

  const renderTabs = () => {
    return (
      tabs &&
      !isEmptyArray(tabs?.entry) &&
      tabs.entry.map((entry, index) => (
        <Tab
          key={entry.id}
          className={twMerge(
            tabItemSizingClassName,
            tabItemInnerAlignmentClassName,
            'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap'
          )}
          selectedClassName="outline-0"
        >
          <ButtonCell
            selected={index === tabIndex}
            cellIds={tabCellIds}
            entry={entry}
          />
        </Tab>
      ))
    );
  };

  const tabsBarContainer = useMemo(
    () => (
      <div
        ref={tabListRef}
        className={twMerge(
          'tabs-bar-container',
          'w-full',
          'tab-container-horizontal-gutter scrollbar-hide',
          'tabs-bar-container-border',
          borderTabsContainerStyle,
          overflowClasses,
          stackingClasses,
          verticalGutterClass,
          tabItemsContainerClassName,
          tabItemsAlignmentClassName
        )}
      >
        {renderTabs()}
      </div>
    ),
    [
      tabListRef,
      borderTabsContainerStyle,
      overflowClasses,
      stackingClasses,
      verticalGutterClass,
      tabItemsContainerClassName,
      tabItemsAlignmentClassName,
      tabIndex,
      tabs?.entry,
      tabCellIds,
      tabItemSizingClassName,
      tabItemInnerAlignmentClassName,
    ]
  );

  if (!tabs || isEmptyArray(tabs?.entry)) return <></>;

  const getTabData = (entryId: string, entryType: string) => {
    return screens.find(
      (s) => s.id === entryId || s.id === contentTypes[entryType]?.screen_id
    );
  };

  const renderTabContent = (entry: FeedEntry) => {
    const tab = getTabData(entry?.id as string, entry.type?.value || '');

    if (!tab) return <></>;

    const componentFeed = tabsContentFeeds?.flat();

    return (
      <div className={`screen-${tab.id} screen-classes tab-content z-0`}>
        {React.Children.toArray(
          tab.ui_components.map((uiComponent: any) => {
            const feedData = componentFeed?.find(
              (feed) => feed.componentId === uiComponent.id
            );

            return (
              <div className="truncate">
                <UiComponent
                  uiComponent={uiComponent}
                  feed={feedData?.feed}
                  {...props}
                />
              </div>
            );
          })
        )}
      </div>
    );
  };

  return (
    <div className={`tabs-screen-${screen.id} tabs-screen-classes`}>
      <Tabs
        disableUpDownKeys
        selectedIndex={tabIndex}
        onSelect={handleTabSelect}
        defaultFocus
      >
        <div
          className={twMerge(
            `tabs-parent tabs-parent-container-border`,
            borderParentContainerStyle,
            sticky,
            backgroundBlurStyles,
            parentShadowStyles,
            parentBackgroundBlurStyles
          )}
        >
          <div>
            <div
              className={twMerge('relative', maxWidth, 'mx-auto', shadowStyles)}
            >
              {arrowsSwitch && canScrollLeft && (
                <ArrowButton
                  direction="left"
                  onClick={() => scrollTabs('left')}
                  show={canScrollLeft}
                  styles={styles}
                  uiComponentId={screen.id}
                />
              )}
              <div
                className={twMerge('scrollbar-hide', overflowClasses, arrows)}
              >
                <TabList
                  className={twMerge(
                    scrollable,
                    stackingClasses,
                    'tabs-bar-container-margin'
                  )}
                >
                  {blending ? (
                    <div className={twMerge(blending)}>{tabsBarContainer}</div>
                  ) : (
                    tabsBarContainer
                  )}
                </TabList>
              </div>
              {arrowsSwitch && canScrollRight && (
                <ArrowButton
                  direction="right"
                  onClick={() => scrollTabs('right')}
                  show={canScrollRight}
                  styles={styles}
                  uiComponentId={screen.id}
                />
              )}
            </div>
          </div>
        </div>
        {tabs.entry.map((entry) => (
          <TabPanel className="h-fit w-full flex-col" key={entry.id}>
            {renderTabContent(entry)}
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
}
